import { gcm } from '@noble/ciphers/aes.js'
import { randomBytes } from '@noble/ciphers/utils.js'
import {
  wordArrayToUint8Array,
  base64ToUint8Array,
  uint8ArrayToBase64
} from './base64'
import SHA256 from 'crypto-js/sha256'
/**
 * AES-GCM 加密/解密工具类
 * 与后端 Java AESUtil 兼容
 *
 * 加密流程：
 * 1. 使用 SHA-256 对密钥进行哈希处理，生成 256 位 AES 密钥
 * 2. 生成 12 字节随机 IV
 * 3. 使用 AES-GCM 模式加密（认证标签长度 128 位）
 * 4. 将 IV + 密文（含认证标签）拼接后进行 Base64 编码
 *
 * 解密流程：
 * 1. Base64 解码
 * 2. 分离 IV（前 12 字节）和密文
 * 3. 使用相同的密钥派生函数生成 AES 密钥
 * 4. 使用 AES-GCM 模式解密并验证认证标签
 */
export default class AESGCM {
  /**
   * AES-GCM 加密
   * @param {string} content - 待加密的明文内容
   * @param {string} encryptKey - 加密密钥（任意长度字符串）
   * @returns {Promise<string>} Base64 编码的密文
   */
  static async encrypt(content, encryptKey) {
    try {
      // 1. 派生 AES 密钥（SHA-256 哈希）
      const keyMaterial = await this.deriveAesKey(encryptKey)

      // 2. 生成随机 IV（12 字节）
      const iv = randomBytes(12)

      const encoder = new TextEncoder()
      const ciphertextWithTag = gcm(keyMaterial, iv).encrypt(
        encoder.encode(content)
      )
      // 4. 拼接 IV + 密文（含认证标签）
      const ciphertextArray = new Uint8Array(ciphertextWithTag)
      const result = new Uint8Array(iv.length + ciphertextArray.length)
      result.set(iv, 0)
      result.set(ciphertextArray, iv.length)
      // 5. Base64 编码
      return uint8ArrayToBase64(result)
    } catch (error) {
      console.error('AES 加密失败:', error)
      throw error
    }
  }

  /**
   * AES-GCM 解密
   * @param {string} encryptStr - Base64 编码的密文
   * @param {string} decryptKey - 解密密钥（需与加密密钥相同）
   * @returns {Promise<string>} 解密后的明文
   */
  static async decrypt(encryptStr, decryptKey) {
    try {
      if (!encryptStr || encryptStr.trim() === '') {
        return null
      }

      // 1. Base64 解码
      const encryptedData = base64ToUint8Array(encryptStr)

      // 2. 分离 IV（前 12 字节）和密文
      const iv = encryptedData.slice(0, 12)
      const ciphertextWithTag = encryptedData.slice(12)

      // 3. 派生 AES 密钥
      const keyMaterial = await this.deriveAesKey(decryptKey)

      // 4. 导入密钥并解密
      // 4. 解密并自动校验认证标签 (失败抛出异常)
      const decrypted = gcm(keyMaterial, iv).decrypt(ciphertextWithTag)
      // 5. 转换为字符串
      const decoder = new TextDecoder()
      return decoder.decode(decrypted)
    } catch (error) {
      console.error('AES 解密失败:', error)
      throw error
    }
  }

  /**
   * 密钥派生函数：使用 SHA-256 将任意字符串哈希为 32 字节（256 位）
   * @param {string} key - 原始密钥字符串
   * @returns {Promise<Uint8Array>} 32 字节的密钥材料
   */
  static async deriveAesKey(key) {
    return wordArrayToUint8Array(SHA256(key))
  }
}
